import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getCoachBookingsApi, getCustomerBookingsDataApi } from '../../../util/api';
import { resolveBookingCode } from '../../../util/bookingCode';
import css from './SupportModal.module.css';

const formatShortDate = value => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
};

const buildBookingLabel = booking => {
  const code = resolveBookingCode(booking);
  const session = booking.sessionName || 'Booking';
  const date = formatShortDate(booking.startTime || booking.bookingDate);
  const party = booking.coachName || booking.customerName;
  const parts = [code ? `#${code}` : null, session, date, party].filter(Boolean);
  return parts.join(' · ');
};

const buildSearchBlob = booking => {
  return [
    booking.id,
    resolveBookingCode(booking),
    booking.sessionName,
    booking.coachName,
    booking.customerName,
    booking.customerEmail,
    booking.status,
  ]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
};

const normalizeBooking = booking => ({
  id: String(booking.id),
  bookingCode: resolveBookingCode(booking),
  sessionName: booking.sessionName,
  startTime: booking.startTime || booking.bookingDate,
  coachName: booking.coachName,
  customerName: booking.customerName,
  status: booking.status,
  label: buildBookingLabel(booking),
  searchBlob: buildSearchBlob(booking),
});

const BookingSearchSelect = ({ currentUser, value, onChange, disabled }) => {
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const containerRef = useRef(null);
  const isCoach = currentUser?.attributes?.profile?.publicData?.coach === true;

  const loadBookings = useCallback(
    async searchTerm => {
      setIsLoading(true);
      setLoadError(null);
      try {
        const merged = [];
        const seen = new Set();

        if (isCoach) {
          const coachRes = await getCoachBookingsApi({
            page: 1,
            perPage: 50,
            status: 'All Bookings',
            search: searchTerm || undefined,
          });
          (coachRes?.bookings || []).forEach(b => {
            const id = String(b.id);
            if (!seen.has(id)) {
              seen.add(id);
              merged.push(normalizeBooking(b));
            }
          });
        }

        const now = new Date();
        const start = new Date(now);
        start.setFullYear(start.getFullYear() - 1);
        const end = new Date(now);
        end.setFullYear(end.getFullYear() + 1);

        const customerRes = await getCustomerBookingsDataApi({
          start: start.toISOString(),
          end: end.toISOString(),
        });
        (customerRes?.bookings || []).forEach(b => {
          const id = String(b.id);
          if (!seen.has(id)) {
            seen.add(id);
            merged.push(normalizeBooking(b));
          }
        });

        const lower = (searchTerm || '').trim().toLowerCase();
        const filtered = lower
          ? merged.filter(b => b.searchBlob.includes(lower) || b.label.toLowerCase().includes(lower))
          : merged;

        filtered.sort((a, b) => {
          const ta = a.startTime ? new Date(a.startTime).getTime() : 0;
          const tb = b.startTime ? new Date(b.startTime).getTime() : 0;
          return tb - ta;
        });

        setOptions(filtered.slice(0, 50));
      } catch (err) {
        console.error('[BookingSearchSelect]', err);
        setLoadError('Could not load bookings');
        setOptions([]);
      } finally {
        setIsLoading(false);
      }
    },
    [isCoach]
  );

  useEffect(() => {
    if (!currentUser) return undefined;
    const timer = setTimeout(() => {
      loadBookings(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [currentUser, query, loadBookings]);

  useEffect(() => {
    if (value?.label) {
      setQuery(value.label);
    } else if (!value) {
      setQuery('');
    }
  }, [value?.id, value?.label]);

  useEffect(() => {
    const handleClickOutside = e => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const visibleOptions = useMemo(() => options, [options]);

  const handleSelect = option => {
    onChange(option);
    setQuery(option.label);
    setIsOpen(false);
  };

  const handleClear = () => {
    onChange(null);
    setQuery('');
    setIsOpen(false);
  };

  const handleInputChange = e => {
    const next = e.target.value;
    setQuery(next);
    setIsOpen(true);
    if (value && next !== value.label) {
      onChange(null);
    }
  };

  return (
    <div className={css.bookingSelectWrap} ref={containerRef}>
      <input
        id="support-booking"
        type="text"
        className={css.input}
        value={query}
        onChange={handleInputChange}
        onFocus={() => setIsOpen(true)}
        placeholder="Search by booking #, session, or name..."
        disabled={disabled}
        autoComplete="off"
        aria-autocomplete="list"
        aria-expanded={isOpen}
        aria-controls="support-booking-listbox"
      />
      {value ? (
        <button
          type="button"
          className={css.bookingClearButton}
          onClick={handleClear}
          disabled={disabled}
          aria-label="Clear selected booking"
        >
          ×
        </button>
      ) : null}
      {isOpen && (visibleOptions.length > 0 || isLoading || loadError) ? (
        <ul id="support-booking-listbox" className={css.bookingDropdown} role="listbox">
          {isLoading ? (
            <li className={css.bookingDropdownMuted}>Loading bookings...</li>
          ) : null}
          {loadError ? <li className={css.bookingDropdownMuted}>{loadError}</li> : null}
          {!isLoading && !loadError && visibleOptions.length === 0 ? (
            <li className={css.bookingDropdownMuted}>No bookings found</li>
          ) : null}
          {visibleOptions.map(option => (
            <li
              key={option.id}
              role="option"
              aria-selected={value?.id === option.id}
              className={css.bookingDropdownItem}
              onMouseDown={e => {
                e.preventDefault();
                handleSelect(option);
              }}
            >
              <span className={css.bookingDropdownLabel}>{option.label}</span>
              <span className={css.bookingDropdownMeta}>
                {option.bookingCode ? `#${option.bookingCode}` : option.id.slice(-8)}
                {option.status ? ` · ${option.status}` : ''}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
};

export default BookingSearchSelect;
